import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell } from "@/components/site/AuthShell";
import { FieldError, fieldErrors, ProblemAlert } from "@/components/site/Feedback";
import { api } from "@/lib/api";
import { authStore } from "@/lib/auth-store";

export function DeleteAccountPage() {
  usePageTitle("Delete account");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.account.deleteAccount(password),
    onSuccess: async () => {
      // The account no longer exists: revoking the refresh token may fail, so the local session is cleared too.
      await api.auth.logout();
      authStore.clear();
      queryClient.clear();
      toast.success("Your account was deleted");
      await navigate({ to: "/" });
    },
  });

  return (
    <AuthShell
      eyebrow="Account"
      title="Delete your account"
      subtitle={
        <>
          This permanently deletes your account and anonymises your personal data. Any active membership is
          cancelled straight away, and unused mentorship or analyzer credits are lost. Records we must keep by law
          (e.g. invoices) are retained as described in our{" "}
          <Link to="/legal/privacy" className="underline underline-offset-4">
            Privacy Policy
          </Link>
          .
        </>
      }
    >
      <form
        className="space-y-5"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          remove.mutate();
        }}
      >
        <ProblemAlert error={remove.error} />
        <div className="space-y-2">
          <Label htmlFor="password">Confirm with your password</Label>
          <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <FieldError messages={fieldErrors(remove.error, "Password")} />
        </div>
        <label className="flex items-start gap-3 text-sm">
          <input type="checkbox" className="mt-1 accent-[var(--destructive)]" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          I understand that this cannot be undone.
        </label>
        <Button type="submit" variant="destructive" size="lg" className="w-full" disabled={!password || !confirmed || remove.isPending}>
          {remove.isPending && <Loader2 className="animate-spin" />}
          Delete my account permanently
        </Button>
        <Button asChild variant="outline" size="lg" className="w-full">
          <Link to="/profile">Keep my account</Link>
        </Button>
      </form>
    </AuthShell>
  );
}
